import { Spin, message } from "antd";
import { User, getAuth } from "firebase/auth";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGuardedEffect } from "../../hooks/useGuardedEffect";
import { useOrgInfoStore } from "../../store/org-info/useOrgInfoStore";
import { useAuthFetch } from "../Login/hook";
import { Splash } from "../Splash";

export const OnboardingRedirect: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const handleError = useCallback(
    (err: any) => {
      if (!err) return;
      console.error("Error loading account", err);
      messageApi.open({
        type: "error",
        content: (
          <p style={{ textAlign: "left", whiteSpace: "pre-wrap" }}>
            {`P0 could not read your account information. Reload the page or try signing in in a new browser window.\n
Cause:\n${err?.message ?? err}`}
          </p>
        ),
        // Give enough time for the user to read the error message
        duration: 15,
      });
    },
    [messageApi]
  );

  const auth = useMemo(() => getAuth(), []);

  const authFetch = useAuthFetch(handleError);

  const [user, setUser] = useState<User | null>();
  const { orgInfo, setOrgInfo } = useOrgInfoStore();

  useEffect(() => {
    if (auth.currentUser) {
      setUser(auth.currentUser);
    } else {
      return auth.onAuthStateChanged((user) => {
        setUser(user);
      });
    }
  }, [auth, auth.currentUser]);

  useGuardedEffect(
    (cancellation) => async () => {
      if (user && orgInfo.status !== "found") {
        const response = await authFetch("account", {
          method: "GET",
        });
        if (!response) return;
        const data = await response.json();
        cancellation.guard(setOrgInfo)({
          status: "found",
          info: data,
        });
      }
    },
    [authFetch, user, setOrgInfo, orgInfo.status],
    handleError
  );

  return (
    <>
      {contextHolder}
      {orgInfo.status === "found" ? (
        children
      ) : orgInfo.status === "loading" ? (
        <Splash>
          <div style={{ textAlign: "center" }}>
            <Spin />
          </div>
        </Splash>
      ) : (
        children
      )}
    </>
  );
};
